'use client';

import { claimPendingUseCase, connectUseCaseOf } from '@/@connect/conversation-use-case';
import { hasViewAsSession } from '@/@subsidiary/view-as-store';
import { EVENTS, tracking } from '@/lib/features/tracking';
import { useFeatureFlag } from '@/@feature-flags/hooks';
import { UserRole } from '@/@portal/types';
import { recordViewAsAction } from '@/@subsidiary/view-as-lifecycle';
import { useSession } from '@/providers/enhanced-session';
import { toast } from 'react-toastify';
import { useFeatureFlags } from '@/@feature-flags/hooks/useFeatureFlags';
import {
    isBriefFile,
    routeFeasibilitySend,
    type RoutedSend,
} from '@/@feasibility-dani/components/chat/feasibility-routing';
import { useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
    AgentModes,
    PLANNER_MODE_MESSAGES,
    QUERY_KEYS,
    ROLE_TYPES,
    STREAMING_EVENT_TYPES,
} from '../constants';
import { useStudioAI, useStudioAIViewAs } from '../context';
import type { ChatInputSendOptions } from '../props';
import { studioAIService } from '../services';
import type { AgentOption, AgentOptionsPayload } from '../types';
import { formatDocumentForPrompt } from '../utils/document-extraction';
import { reelsTurnStore } from '../reels/reels-turn-store';
import { isVideoIntent } from '../reels/video-intent';
import { generateConversationId } from '../utils/message-utils';
import {
    claimNextStartEntry,
    noteConversationStart,
    takeFirstDataAnswer,
} from '../utils/first-data-answer';
import { PLANNER_STATUS_LABELS, useActivePlannerReports } from './useActivePlannerReports';
import { useFileUpload } from './useFileUpload';
import { useOrgTrialUsageLimit } from './useOrgTrialUsageLimit';
import { useStudioCreditAlerts } from './useStudioCreditAlerts';

/**
 * Surfaces a send failure to the user. `showErrorToast`
 * (`@/components/ui/molecules/Toast`) renders through HeroUI's `addToast`,
 * which has no `<ToastProvider>` mounted anywhere in this app — it silently
 * no-ops. `react-toastify`'s `toast`, mounted in `providers/heroui.tsx`, is
 * the one that actually renders (see its use in `message-actions.tsx`).
 */
function showSendErrorToast(error: unknown) {
    toast.error(error instanceof Error ? error.message : 'Failed to send message', {
        position: 'top-right',
        autoClose: 5000,
    });
}

/**
 * Shared logic for both ChatLanding and ChatConversation page-level components.
 * Encapsulates freemium state, file upload, agent mode handling, planner polling,
 * message sending, and ChatInput prop construction.
 *
 * @param options.conversationId - The conversation ID from the URL (or null/undefined for the landing page).
 */
export function useChatPageCommon(
    options: {
        conversationId?: string | null;
    } = {},
) {
    const { conversationId: urlConversationId = null } = options;

    const router = useRouter();
    const { hasFlag } = useFeatureFlags();
    const ctx = useStudioAI();
    const {
        currentThreadId,
        messages,
        conversations,
        addMessage,
        updateMessage,
        sendMessage,
        sendSilentMessage,
        stopConversation,
        startNewConversation,
        markNavigatingToNewChat,
        duplicateConversation,
        renameConversation,
        isLoading,
        setIsLoading,
        setCurrentStatus,
        setCurrentConversationId,
        setMessages,
        setThreadId,
        setParentMessageId,
        isSharedView,
        dataRefreshDates,
        masqueradeClient,
        setMasqueradeClient,
        isMasqueradeLocked,
        isGlobalViewAsActive,
        agentMode,
        effectiveAgentMode,
        setAgentMode,
        plannerRunId,
        setPlannerRunId,
        plannerTemplateId,
        setPlannerTemplateId,
        plannerReportData,
        smartRoutingEnabled,
        setSmartRoutingEnabled,
        freemiumLimitReached,
        pendingOptions,
        setPendingOptions,
        handleReportKickedOff,
    } = ctx;

    const { data: session } = useSession();
    const isUnaffiliated = session?.user?.isUnaffiliated ?? false;
    const isFreemium = isUnaffiliated;
    const isCanary = session?.user?.groups.includes(UserRole.CANARY);
    const isInternal = session?.user?.isInternal ?? false;
    const isAdmin = session?.user?.isAdmin ?? false;

    // Org-trial path: org has DANI access via studioai_org_access AND the user does
    // not hold a per-user license (per-user-licensed users are unlimited and exempt).
    const hasUserLicense = session?.user?.hasStudioAIAccess ?? false;
    const hasClientStudioAIAccess =
        (session?.org?.hasClientStudioAIAccess ?? false) && !hasUserLicense && !isFreemium;

    const sessionLimitReached = session?.user?.freemiumUsage?.hasReachedLimit ?? false;

    const { data: accessStatus } = useQuery({
        queryKey: QUERY_KEYS.ACCESS_STATUS,
        queryFn: () => studioAIService.getAccessStatus(),
        enabled: isFreemium || hasClientStudioAIAccess,
        staleTime: 1000 * 60 * 5,
        retry: 1,
    });

    const liveUsage = accessStatus?.freemiumUsage;
    const limitReached = sessionLimitReached || liveUsage?.hasReachedLimit || false;

    const creditPercent = (() => {
        const usage = accessStatus?.freemiumUsage ?? accessStatus?.clientStudioAIAccess;
        if (!usage) return 100;
        const limit =
            'limit' in usage ? (usage as { limit: number }).limit : (usage.limitValue ?? 0);
        if (!limit) return 100;
        const remaining = Math.round((1 - usage.currentCount / limit) * 100);
        return Math.max(0, Math.min(100, remaining));
    })();

    const chatInputUsageLimit = useOrgTrialUsageLimit();
    const creditAlerts = useStudioCreditAlerts();

    const [limitModalOpen, setLimitModalOpen] = useState(false);
    const hasAutoOpenedLimitModal = useRef(false);
    const prevFreemiumLimitReached = useRef(false);

    useEffect(() => {
        if (hasAutoOpenedLimitModal.current) return;
        if (isFreemium && limitReached) {
            hasAutoOpenedLimitModal.current = true;
            setLimitModalOpen(true);
        }
    }, [isFreemium, limitReached]);

    useEffect(() => {
        if (freemiumLimitReached && !prevFreemiumLimitReached.current) {
            setLimitModalOpen(true);
        }
        prevFreemiumLimitReached.current = freemiumLimitReached;
    }, [freemiumLimitReached]);

    const fileUpload = useFileUpload();

    const isPlannerFlagEnabled = useFeatureFlag('studio-ai-planner-mode');
    const viewAs = useStudioAIViewAs();
    // Plan mode with a client: only under staff View-as, and only when
    // `isPlannerAllowedForClient` passes (M4S-5670 §4). The DANI-only picker
    // keeps today's chat-only rule.
    const canPlanWithClient =
        !masqueradeClient || (isGlobalViewAsActive && viewAs.plannerAllowedForClient);

    const handleSmartRoutingToggle = useCallback(
        (enabled: boolean | ((previous: boolean) => boolean)) => {
            if (typeof enabled === 'function') {
                setSmartRoutingEnabled((prev) => {
                    const next = enabled(prev);
                    if (isPlannerFlagEnabled && canPlanWithClient) {
                        setAgentMode(next ? AgentModes.PLANNER : null);
                    }
                    return next;
                });
            } else {
                setSmartRoutingEnabled(enabled);
                if (isPlannerFlagEnabled && canPlanWithClient) {
                    setAgentMode(enabled ? AgentModes.PLANNER : null);
                }
            }
        },
        [isPlannerFlagEnabled, canPlanWithClient, setAgentMode, setSmartRoutingEnabled],
    );

    /**
     * Sends a template prompt as a silent message to the plan-mode/chat endpoint.
     * Used by TemplateCards on the landing page (gated behind studio-ai-planner-mode)
     * and by the Reports build form via the /dani?launch=reports handoff, which
     * passes a composed `prompt` (an INPUTS block) instead of the bare title.
     * Passes templateId as a first-class field in the request payload.
     */
    const handleTemplateSelect = useCallback(
        async (template: {
            id: number;
            title: string;
            prompt?: string;
            /**
             * Renames the conversation once it exists, which is also what the
             * finished report is filed and shared under — `studioai_reports` has
             * no title column of its own, so a report's title is its
             * conversation's title.
             *
             * Worth doing because the server-side default is unreliable: real
             * runs have produced both "Audience Profiling - Panera Bread" and
             * "New Report - 2026-04-06T15:32:27.513Z". A caller that knows the
             * brand and project (the Reports builders do) can always do better.
             */
            reportTitle?: string;
        }) => {
            if (urlConversationId || messages.length > 0) {
                setMessages([]);
                setThreadId(null);
                setParentMessageId('0');
                setCurrentConversationId(null);
                setPlannerRunId(null);
            }

            setAgentMode(AgentModes.PLANNER);
            setSmartRoutingEnabled(true);
            setPlannerTemplateId(template.id);
            setIsLoading(true);
            setCurrentStatus(
                `${PLANNER_MODE_MESSAGES.ACTIVATING_STATUS.message}|${PLANNER_MODE_MESSAGES.ACTIVATING_STATUS.icon}`,
            );

            const newConversationId = generateConversationId();
            setCurrentConversationId(newConversationId);
            router.push(`/dani/${newConversationId}`, { scroll: false });
            try {
                await sendSilentMessage({
                    prompt: template.prompt ?? template.title,
                    conversationId: newConversationId,
                    threadId: '0',
                    parentMessageId: '0',
                    agentMode: AgentModes.PLANNER,
                    templateId: template.id,
                });

                // Under staff View-as, record the run against the session
                // (M4S-5670 §7). A no-op when the tab is not viewing as a
                // client; the server keeps one row per conversation.
                recordViewAsAction('report_run', newConversationId, { templateId: template.id });

                // Best-effort, and deliberately not awaited into the failure
                // path above: the conversation only exists server-side once the
                // silent message lands, and if the rename misses, the server's
                // own title stands. A worse title is not worth failing a run
                // that otherwise succeeded.
                if (template.reportTitle) {
                    void renameConversation(newConversationId, template.reportTitle).catch(
                        () => undefined,
                    );
                }
            } catch {
                setIsLoading(false);
                setCurrentStatus(null);
                addMessage({
                    id: `template_start_error_${Date.now()}`,
                    role: ROLE_TYPES.ASSISTANT,
                    content: [
                        {
                            type: STREAMING_EVENT_TYPES.TEXT,
                            text: `I couldn't start the "${template.title}" template right now. Please try again.`,
                        },
                    ],
                    isStreaming: false,
                    timestamp: new Date(),
                });
            }
        },
        [
            urlConversationId,
            messages.length,
            setMessages,
            setThreadId,
            setParentMessageId,
            setCurrentConversationId,
            setPlannerRunId,
            setPlannerTemplateId,
            addMessage,
            sendSilentMessage,
            setAgentMode,
            setSmartRoutingEnabled,
            setIsLoading,
            setCurrentStatus,
            renameConversation,
            router,
        ],
    );

    const { getEntry: getActiveReportEntry } = useActivePlannerReports();

    const activeReportEntry = getActiveReportEntry(plannerRunId);
    // DANI saying the report is ready (or failed) beats an older polled status:
    // the poll runs every 20 s and must not hold the bar at an earlier step.
    // Kickoff clears plannerReportData, so a terminal one belongs to this run.
    const reportDataIsTerminal =
        plannerReportData?.status === 'COMPLETE' || plannerReportData?.status === 'FAILED';
    const plannerRunStatus = reportDataIsTerminal
        ? plannerReportData.status
        : (activeReportEntry?.lastStatus ?? plannerReportData?.status ?? null);
    // Lock the chat input only when there's positive evidence of an in-flight
    // run. A bare `plannerRunId` with no known status (e.g. rehydrated from a
    // stale conversation, expired localStorage entry, or lingering after a
    // landing-page reset) must not pin the input into "stop" mode.
    const isPlannerReportActive =
        !!plannerRunId &&
        !!plannerRunStatus &&
        plannerRunStatus !== 'COMPLETE' &&
        plannerRunStatus !== 'FAILED';

    const plannerStatus = {
        data:
            !reportDataIsTerminal && activeReportEntry?.lastStatus
                ? {
                      status: activeReportEntry.lastStatus,
                      reportId: activeReportEntry.lastReportId,
                      reportUrl: activeReportEntry.lastReportUrl,
                  }
                : plannerReportData?.status
                  ? {
                        status: plannerReportData.status,
                        reportId: plannerReportData.reportId,
                        reportUrl: plannerReportData.reportUrl,
                    }
                  : undefined,
    };

    // Update the kickoff chat message live as the run status changes so the
    // user can see PLANNED → INSIGHTS_READY → RUNNING → COMPLETE in-thread.
    // The message was added with a stable id by handleReportKickedOff; if it's
    // gone (e.g. page refresh before kickoff) updateMessage silently no-ops.
    useEffect(() => {
        if (!plannerRunId || !plannerRunStatus) return;
        const messageId = `report_kickoff_${plannerRunId}`;

        const STATUS_PERCENT: Record<string, number> = {
            NOT_FOUND: 5,
            PLANNED: 15,
            INSIGHTS_READY: 40,
            RUNNING: 65,
            ANALYSES_COMPLETE: 85,
            COMPLETE: 100,
            FAILED: 100,
        };

        let text: string;
        if (plannerRunStatus === 'COMPLETE') {
            text = '**Report is ready!** Your PDF is available in History.';
        } else if (plannerRunStatus === 'FAILED') {
            text = '**Report generation failed.** Please try again or contact support.';
        } else {
            text =
                'Your report is being generated. This can take 15–30 minutes. You can leave this page and come back anytime — your report will be waiting for you.';
        }

        const percent = STATUS_PERCENT[plannerRunStatus] ?? 5;

        updateMessage(messageId, {
            content: [
                { type: STREAMING_EVENT_TYPES.TEXT, text },
                {
                    type: STREAMING_EVENT_TYPES.REPORT_PROGRESS,
                    report_progress: { percent, status: plannerRunStatus },
                },
            ],
        });
    }, [plannerRunId, plannerRunStatus, updateMessage]);

    // Phase 0 of DANI memory: record a client's first data answer in a conversation
    // that started in this tab (A1). Staff and View-as are left out of the metric.
    useEffect(() => {
        if (isInternal || hasViewAsSession()) return;
        const firstDataAnswer = takeFirstDataAnswer(urlConversationId, messages);
        if (firstDataAnswer) {
            tracking.event(EVENTS.DANI_ANSWERS.FIRST_DATA_ANSWER, { ...firstDataAnswer });
        }
    }, [messages, urlConversationId, isInternal]);

    const clearPendingOptions = useCallback(() => setPendingOptions(null), [setPendingOptions]);

    const pendingOptionsRef = useRef<AgentOptionsPayload | null>(pendingOptions);
    useEffect(() => {
        pendingOptionsRef.current = pendingOptions;
    }, [pendingOptions]);

    /**
     * Restore a previously-cleared chip set after a failed send, but only if
     * nothing else has repopulated `pendingOptions` in the meantime (e.g. a
     * streamed `agent_options` event arriving before the failure). This avoids
     * clobbering fresh options that belong to the next turn.
     */
    const restorePendingOptionsOnFailure = useCallback(
        (snapshot: AgentOptionsPayload | null) => {
            if (snapshot && pendingOptionsRef.current === null) {
                setPendingOptions(snapshot);
            }
        },
        [setPendingOptions],
    );

    const handleSendMessage = useCallback(
        async (content: string, sendOptions?: ChatInputSendOptions) => {
            if (!content.trim()) return;

            // Typing a free-form reply clears any pending options.
            clearPendingOptions();

            const docToSend = fileUpload.extractedDocument;
            const conversationId = urlConversationId || generateConversationId();
            const isNewConversation = !urlConversationId;
            if (isNewConversation) claimPendingUseCase(conversationId);

            let prompt = docToSend ? formatDocumentForPrompt(docToSend, content) : content;

            // Feasibility starts on its own for staff: a feasibility ask gets DANI's rules,
            // and a brief attached in a feasibility chat is read by Studio's brief reader.
            let routed: RoutedSend | null = null;
            if (isInternal && hasFlag('feasibility-dani')) {
                try {
                    if (fileUpload.uploadedFile?.file && isBriefFile(fileUpload.uploadedFile.name))
                        toast.info('Reading the file for the feasibility check…', {
                            autoClose: 4000,
                        });
                    routed = await routeFeasibilitySend({
                        content,
                        conversationId,
                        isNewConversation,
                        messages,
                        file: fileUpload.uploadedFile?.file ?? null,
                    });
                } catch (err) {
                    toast.error(
                        `Feasibility could not read that: ${err instanceof Error ? err.message : 'unknown error'}. It goes to DANI as it is.`,
                    );
                }
            }
            if (routed) prompt = routed.prompt;

            if (isNewConversation) {
                markNavigatingToNewChat();
                router.push(`/dani/${conversationId}`, { scroll: false });
            }

            // Build context data, merging the document attachment payload with the smart-routing flag.
            const effectiveSmartRoutingEnabled =
                sendOptions?.smartRoutingEnabled ?? smartRoutingEnabled;
            const contextData: Record<string, unknown> = {
                smartRoutingEnabled: effectiveSmartRoutingEnabled,
            };
            if (routed?.displayMessage) contextData.displayMessage = routed.displayMessage;
            if (docToSend && !routed) {
                contextData.displayMessage = content;
                contextData.attachedDocument = {
                    fileName: docToSend.fileName,
                    contentType: docToSend.contentType,
                    size: docToSend.size,
                    extractedText: docToSend.extractedText,
                    extractionMethod: docToSend.extractionMethod,
                };
            }

            // Forward the active plan template id on every plan-mode turn.
            const plannerTemplateIdForRequest =
                effectiveAgentMode === AgentModes.PLANNER && plannerTemplateId != null
                    ? plannerTemplateId
                    : undefined;

            // A conversation about a video study (attached, or asked about now)
            // goes to the Reels agent; everything else goes to DANI unchanged.
            if (isNewConversation) reelsTurnStore.claimPending(conversationId);
            const reelsRecord = reelsTurnStore.get(conversationId);
            const toReels =
                !isFreemium &&
                !docToSend &&
                effectiveAgentMode !== AgentModes.PLANNER &&
                (reelsRecord.study !== null || isVideoIntent(content));

            // Phase 0 of DANI memory: note how this conversation began, so its first data
            // answer can be counted (utils/first-data-answer.ts). The deep-link mark is read
            // on every new conversation, so it never carries over to a later one.
            if (isNewConversation) {
                const markedEntry = claimNextStartEntry();
                noteConversationStart(conversationId, {
                    entry: routed
                        ? 'feasibility'
                        : connectUseCaseOf(conversationId)
                          ? 'connect'
                          : (markedEntry ?? (messages.length > 0 ? 'other' : 'typed')),
                    kind: toReels
                        ? 'reels'
                        : effectiveAgentMode === AgentModes.PLANNER
                          ? 'planner'
                          : 'chat',
                    startIndex: messages.length,
                });
            }

            try {
                await sendMessage({
                    prompt,
                    conversationId,
                    parentMessageId: '0',
                    agentMode: effectiveAgentMode,
                    contextData,
                    templateId: plannerTemplateIdForRequest,
                    reels: toReels
                        ? { study: reelsRecord.study, history: reelsRecord.turns }
                        : undefined,
                    ...(routed?.previousMessages
                        ? { threadId: '0', previousMessages: routed.previousMessages }
                        : {}),
                });
                fileUpload.remove();
            } catch (err) {
                // The context records the failure (chatState/error), but nothing
                // reads that back out — without a toast here, a failed first
                // message just leaves the user staring at their own question
                // with no reply and no explanation.
                showSendErrorToast(err);
            }
        },
        [
            urlConversationId,
            effectiveAgentMode,
            sendMessage,
            fileUpload,
            router,
            clearPendingOptions,
            smartRoutingEnabled,
            plannerTemplateId,
            markNavigatingToNewChat,
            isFreemium,
            isInternal,
            hasFlag,
            messages,
        ],
    );

    const handleSelectOption = useCallback(
        async (option: AgentOption) => {
            // Snapshot before clearing so a transient send failure doesn't leave the user stranded with no way to retry their choice.
            const snapshot = pendingOptions;
            clearPendingOptions();
            const conversationId = urlConversationId || generateConversationId();
            const isNewConversation = !urlConversationId;
            if (isNewConversation) {
                markNavigatingToNewChat();
                router.push(`/dani/${conversationId}`, { scroll: false });
            }
            const plannerTemplateIdForRequest =
                effectiveAgentMode === AgentModes.PLANNER && plannerTemplateId != null
                    ? plannerTemplateId
                    : undefined;
            try {
                await sendMessage({
                    prompt: option.label,
                    conversationId,
                    parentMessageId: '0',
                    agentMode: effectiveAgentMode,
                    contextData: {
                        isOptionSelection: true,
                        optionId: option.id,
                        option_selected: {
                            optionId: option.id,
                            label: option.label,
                            fromOptions: snapshot?.options ?? [],
                        },
                        ...(option.extraContext ?? {}),
                    },
                    templateId: plannerTemplateIdForRequest,
                });
            } catch (err) {
                restorePendingOptionsOnFailure(snapshot);
                showSendErrorToast(err);
            }
        },
        [
            urlConversationId,
            effectiveAgentMode,
            sendMessage,
            clearPendingOptions,
            restorePendingOptionsOnFailure,
            router,
            pendingOptions,
            plannerTemplateId,
            markNavigatingToNewChat,
        ],
    );

    const handleSelectMultiple = useCallback(
        async (selected: AgentOption[]) => {
            // Snapshot before clearing so a transient send failure doesn't
            // leave the user stranded with no way to retry their choice.
            const snapshot = pendingOptions;
            clearPendingOptions();
            const labels = selected.map((o) => o.label);
            const conversationId = urlConversationId || generateConversationId();
            const isNewConversation = !urlConversationId;
            if (isNewConversation) {
                markNavigatingToNewChat();
                router.push(`/dani/${conversationId}`, { scroll: false });
            }
            // Build the "fromOptions" set the backend sees.
            const predefined = snapshot?.options ?? [];
            const predefinedIds = new Set(predefined.map((o) => o.id));
            const customFromSelection = selected.filter((o) => !predefinedIds.has(o.id));
            const fromOptions = [...predefined, ...customFromSelection];
            const plannerTemplateIdForRequest =
                effectiveAgentMode === AgentModes.PLANNER && plannerTemplateId != null
                    ? plannerTemplateId
                    : undefined;
            try {
                await sendMessage({
                    prompt: labels.join(', '),
                    conversationId,
                    parentMessageId: '0',
                    agentMode: effectiveAgentMode,
                    contextData: {
                        isOptionSelection: true,
                        optionId: selected.map((o) => o.id).join(','),
                        option_selected: {
                            optionId: selected.map((o) => o.id).join(','),
                            label: labels.join(', '),
                            fromOptions,
                        },
                    },
                    templateId: plannerTemplateIdForRequest,
                });
            } catch (err) {
                restorePendingOptionsOnFailure(snapshot);
                showSendErrorToast(err);
            }
        },
        [
            urlConversationId,
            effectiveAgentMode,
            sendMessage,
            clearPendingOptions,
            restorePendingOptionsOnFailure,
            router,
            pendingOptions,
            plannerTemplateId,
            markNavigatingToNewChat,
        ],
    );

    const handleSkipOptions = useCallback(() => {
        clearPendingOptions();
        const conversationId = urlConversationId || generateConversationId();
        const isNewConversation = !urlConversationId;
        if (isNewConversation) {
            markNavigatingToNewChat();
            router.push(`/dani/${conversationId}`, { scroll: false });
        }
        const plannerTemplateIdForRequest =
            effectiveAgentMode === AgentModes.PLANNER && plannerTemplateId != null
                ? plannerTemplateId
                : undefined;
        sendMessage({
            prompt: 'Skip',
            conversationId,
            parentMessageId: '0',
            agentMode: effectiveAgentMode,
            templateId: plannerTemplateIdForRequest,
        }).catch(showSendErrorToast);
    }, [
        urlConversationId,
        effectiveAgentMode,
        sendMessage,
        clearPendingOptions,
        router,
        plannerTemplateId,
        markNavigatingToNewChat,
    ]);

    const sharedChatInputProps = {
        conversationId: urlConversationId,
        onSendMessage: handleSendMessage,
        isLoading: isLoading || isPlannerReportActive,
        handleStop: stopConversation,
        onFileUpload: isFreemium ? undefined : fileUpload.upload,
        uploadedFile: isFreemium ? null : fileUpload.uploadedFile,
        onFileRemove: isFreemium ? undefined : fileUpload.remove,
        isExtracting: isFreemium ? false : fileUpload.isExtracting,
        extractionProgress: isFreemium ? null : fileUpload.extractionProgress,
        showSmartRoutingToggle: isPlannerFlagEnabled && canPlanWithClient,
        smartRoutingEnabled,
        setSmartRoutingEnabled: handleSmartRoutingToggle,
        usageLimit: chatInputUsageLimit,
        trialStrip: creditAlerts.trialStrip,
        isCreditGatePaused: creditAlerts.isPaused,
        placeholder: creditAlerts.isPaused
            ? (creditAlerts.pausePlaceholder ?? undefined)
            : undefined,
        lockedReason: viewAs.inputLockedReason,
    } as const;

    /**
     * Option chip handlers live beside `ChatMessages`
     */
    const sharedChatMessagesOptionProps = {
        onSelectOption: handleSelectOption,
        onSelectMultiple: handleSelectMultiple,
        onSkipOptions: handleSkipOptions,
    } as const;

    return {
        router,
        conversationId: urlConversationId,
        currentThreadId,
        messages,
        conversations,
        isLoading,
        startNewConversation,
        duplicateConversation,
        renameConversation,
        stopConversation,
        isSharedView,
        dataRefreshDates,
        masqueradeClient,
        setMasqueradeClient,
        isMasqueradeLocked,
        isGlobalViewAsActive,
        agentMode,
        plannerRunId,
        plannerReportData,
        session,
        isUnaffiliated,
        isFreemium,
        isCanary,
        isInternal,
        isAdmin,
        limitReached,
        limitModalOpen,
        setLimitModalOpen,
        liveUsage,
        creditPercent,
        fileUpload,
        plannerStatus,
        plannerRunStatus,
        isPlannerReportActive,
        handleReportKickedOff,
        handleSendMessage,
        handleTemplateSelect,
        sharedChatInputProps,
        sharedChatMessagesOptionProps,
        creditAlerts,
        pendingOptions,
        clearPendingOptions,
    };
}
